"use client";

interface PricingSectionProps {
	onJoinWaitlist?: () => void;
}

export function PricingSection({ onJoinWaitlist }: PricingSectionProps) {
	const features = [
		"Personalized answers based on your data",
		"Training analysis and insights",
		"Training plan assistance",
		"Integrations with Strava & Garmin",
		"Unlimited conversations",
		"Priority support",
	];

	return (
		<div className="w-full border-b border-[rgba(55,50,47,0.12)] flex flex-col justify-center items-center">
			{/* Header Section */}
			<div className="self-stretch px-6 md:px-24 py-12 md:py-16 border-b border-[rgba(55,50,47,0.12)] flex justify-center items-center">
				<div className="w-full max-w-[586px] flex flex-col justify-start items-center gap-4">
					{/* Title */}
					<h2 className="self-stretch text-center flex justify-center flex-col text-[#49423D] text-3xl md:text-5xl font-semibold leading-tight md:leading-[60px] font-sans tracking-tight">
						Simple, transparent pricing
					</h2>

					{/* Description */}
					<div className="self-stretch text-center text-[#605A57] text-base font-normal leading-7 font-sans">
						Start your 3-day free trial. Cancel anytime.
					</div>
				</div>
			</div>

			{/* Pricing Cards Section */}
			<div className="self-stretch py-12 md:py-16 flex justify-center items-center px-6">
				<div className="flex flex-col md:flex-row justify-center items-stretch gap-6 w-full max-w-[800px]">
					{/* Monthly Plan */}
					<div className="flex-1 px-6 py-8 border border-[rgba(50,45,43,0.12)] rounded-lg overflow-hidden flex flex-col justify-start items-start gap-8 bg-white relative">
						{/* Trial Badge */}
						<div className="absolute top-6 right-6">
							<div className="px-3 py-1 bg-[#37322F] rounded-full">
								<div className="text-white text-xs font-medium leading-4 font-sans">
									3-day trial
								</div>
							</div>
						</div>

						{/* Plan Header */}
						<div className="self-stretch flex flex-col justify-start items-start gap-6">
							<div className="self-stretch flex flex-col justify-start items-start gap-2">
								<div className="text-[rgba(55,50,47,0.90)] text-lg font-medium leading-7 font-sans">
									Monthly
								</div>
								<div className="text-[rgba(41,37,35,0.70)] text-sm font-normal leading-5 font-sans">
									Perfect for trying out Koach
								</div>
							</div>

							<div className="self-stretch flex flex-col justify-start items-start gap-1">
								<div className="flex items-baseline gap-1">
									<span className="text-[#37322F] text-5xl font-medium leading-[60px] font-serif">
										$10
									</span>
									<span className="text-[#847971] text-sm font-medium font-sans">
										/month
									</span>
								</div>
								<div className="text-transparent text-xs font-normal leading-4 font-sans select-none">
									.
								</div>
							</div>

							<button
								type="button"
								onClick={onJoinWaitlist}
								className="self-stretch px-4 py-[10px] relative bg-[#37322F] shadow-[0px_2px_4px_rgba(55,50,47,0.12)] overflow-hidden rounded-[99px] flex justify-center items-center hover:bg-[#49423D] transition-colors border-0 cursor-pointer"
							>
								<div className="w-full h-[41px] absolute left-0 top-[-0.5px] bg-gradient-to-b from-[rgba(255,255,255,0.20)] to-[rgba(0,0,0,0.10)] mix-blend-multiply" />
								<div className="flex justify-center flex-col text-[#FBFAF9] text-[13px] font-medium leading-5 font-sans relative z-10">
									Join the Waitlist
								</div>
							</button>
						</div>

						{/* Features */}
						<div className="self-stretch flex flex-col justify-start items-start gap-3">
							{features.map((feature) => (
								<div
									key={feature}
									className="self-stretch flex justify-start items-start gap-3"
								>
									<div className="w-5 h-5 relative flex items-center justify-center mt-0.5">
										<svg
											width="16"
											height="16"
											viewBox="0 0 16 16"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
											role="img"
											aria-label="Checkmark"
										>
											<title>Checkmark</title>
											<path
												d="M13.3333 4L6 11.3333L2.66667 8"
												stroke="#37322F"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</div>
									<div className="flex-1 text-[rgba(55,50,47,0.80)] text-sm font-normal leading-6 font-sans">
										{feature}
									</div>
								</div>
							))}
						</div>
					</div>

					{/* Annual Plan */}
					<div className="flex-1 px-6 py-8 bg-[#37322F] border border-[rgba(55,50,47,0.12)] rounded-lg overflow-hidden flex flex-col justify-start items-start gap-8 relative">
						{/* Trial Badge */}
						<div className="absolute top-6 right-6">
							<div className="px-3 py-1 bg-white rounded-full">
								<div className="text-[#37322F] text-xs font-medium leading-4 font-sans">
									3-day trial
								</div>
							</div>
						</div>

						{/* Plan Header */}
						<div className="self-stretch flex flex-col justify-start items-start gap-6">
							<div className="self-stretch flex flex-col justify-start items-start gap-2">
								<div className="flex items-center gap-2">
									<div className="text-[#FBFAF9] text-lg font-medium leading-7 font-sans">
										Annually
									</div>
									<div className="px-2 py-0.5 bg-[rgba(255,128,0,0.15)] rounded">
										<div className="text-[#FF8000] text-xs font-medium leading-4 font-sans">
											Save 17%
										</div>
									</div>
								</div>
								<div className="text-[#B2AEA9] text-sm font-normal leading-5 font-sans">
									Best value for committed runners
								</div>
							</div>

							<div className="self-stretch flex flex-col justify-start items-start gap-1">
								<div className="flex items-baseline gap-1">
									<span className="text-[#F0EFEE] text-5xl font-medium leading-[60px] font-serif">
										$100
									</span>
									<span className="text-[#D2C6BF] text-sm font-medium font-sans">
										/year
									</span>
								</div>
								<div className="text-[#B2AEA9] text-xs font-normal leading-4 font-sans">
									$8.33 per month
								</div>
							</div>

							<button
								type="button"
								onClick={onJoinWaitlist}
								className="self-stretch px-4 py-[10px] relative bg-[#FBFAF9] shadow-[0px_2px_4px_rgba(55,50,47,0.12)] overflow-hidden rounded-[99px] flex justify-center items-center hover:bg-white transition-colors border-0 cursor-pointer"
							>
								<div className="w-full h-[41px] absolute left-0 top-[-0.5px] bg-gradient-to-b from-[rgba(255,255,255,0)] to-[rgba(0,0,0,0.10)] mix-blend-multiply" />
								<div className="flex justify-center flex-col text-[#37322F] text-[13px] font-medium leading-5 font-sans relative z-10">
									Join the Waitlist
								</div>
							</button>
						</div>

						{/* Features */}
						<div className="self-stretch flex flex-col justify-start items-start gap-3">
							{features.map((feature) => (
								<div
									key={feature}
									className="self-stretch flex justify-start items-start gap-3"
								>
									<div className="w-5 h-5 relative flex items-center justify-center mt-0.5">
										<svg
											width="16"
											height="16"
											viewBox="0 0 16 16"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
											role="img"
											aria-label="Checkmark"
										>
											<title>Checkmark</title>
											<path
												d="M13.3333 4L6 11.3333L2.66667 8"
												stroke="#FF8000"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</div>
									<div className="flex-1 text-[#F0EFEE] text-sm font-normal leading-6 font-sans">
										{feature}
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
